import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { COPY } from "@/config/copy";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function LeadForm({ lead, setLead, onSubmit }) {
  const c = COPY.lead_form;
  const b = COPY.brand;
  const f = c.fields;

  const formRef = useRef(null);
  const [errorField, setErrorField] = useState(null);

  const syncField = (k) => (e) => {
    setLead((prev) => ({ ...(prev || {}), [k]: e.target.value }));
    if (errorField === k) setErrorField(null);
  };

  // Read the actual DOM values on submit so browser autofill can't leave stale state.
  const submit = (e) => {
    e.preventDefault();
    const fd = new FormData(formRef.current);
    const name = String(fd.get("name") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const business_name = String(fd.get("business_name") || "").trim();
    const website_url = String(fd.get("website_url") || "").trim();

    if (name.length < 2) {
      setErrorField("name");
      toast.error("Please enter your first name.");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setErrorField("email");
      toast.error("Please enter a valid email address.");
      return;
    }
    if (business_name.length < 2) {
      setErrorField("business_name");
      toast.error("Please enter your business name.");
      return;
    }

    // Sync the DOM values back into state before advancing to the next phase.
    setLead({ name, email, business_name, website_url });
    onSubmit();
  };

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <div className="flex items-center justify-between px-6 md:px-12 pt-10 pb-8 border-b border-[#E5E5E5]">
        <div className="section-label">{b.audit_label}</div>
        <div className="section-label">{c.step_label}</div>
      </div>

      <div className="flex-1 flex items-center justify-center py-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="stage w-full"
        >
          <div className="section-label mb-6">{c.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] font-light display-tight">
            {c.headline}
          </h2>
          <p className="mt-6 text-[#555] leading-relaxed max-w-md mx-auto">{c.subhead}</p>

          <form ref={formRef} onSubmit={submit} className="mt-14 mx-auto max-w-md space-y-8" noValidate>
            <FormField label={f.first_name.label}>
              <input
                type="text"
                name="name"
                required
                defaultValue={lead.name}
                onChange={syncField("name")}
                onBlur={syncField("name")}
                autoComplete="given-name"
                placeholder={f.first_name.placeholder}
                className="sharp-input"
                data-testid="lead-name-input"
              />
            </FormField>
            <FormField label={f.email.label}>
              <input
                type="email"
                name="email"
                required
                defaultValue={lead.email}
                onChange={syncField("email")}
                onBlur={syncField("email")}
                autoComplete="email"
                placeholder={f.email.placeholder}
                className="sharp-input"
                data-testid="lead-email-input"
              />
            </FormField>
            <FormField label={f.business_name.label}>
              <input
                type="text"
                name="business_name"
                required
                defaultValue={lead.business_name}
                onChange={syncField("business_name")}
                onBlur={syncField("business_name")}
                autoComplete="organization"
                placeholder={f.business_name.placeholder}
                className="sharp-input"
                data-testid="lead-business-input"
              />
            </FormField>
            <div>
              <div className="section-label mb-3">
                {f.website.label}{" "}
                <span className="normal-case text-[#999]">{f.website.optional_note}</span>
              </div>
              <input
                type="text"
                name="website_url"
                defaultValue={lead.website_url}
                onChange={syncField("website_url")}
                onBlur={syncField("website_url")}
                autoComplete="url"
                placeholder={f.website.placeholder}
                className="sharp-input"
                data-testid="lead-url-input"
              />
              <div className="mt-3 text-xs text-[#737373] max-w-sm mx-auto leading-relaxed">
                {f.website.helper}
              </div>
            </div>

            <div className="pt-6 flex flex-col items-center gap-4">
              <button type="submit" className="btn-primary" data-testid="submit-lead-btn">
                {c.submit_label}
                <ArrowRight className="btn-arrow" size={16} strokeWidth={1.5} />
              </button>
              <div className="section-label">{c.submit_hint}</div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <div className="section-label mb-3">{label}</div>
      {children}
    </div>
  );
}
